import { useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import { invalidateAllMemberQueries } from "@/react/components/ProfileModalV3/Body/ConnectBanner/util";
import { useToast } from "@/react/components/shared/uikit/ToastV2";
import { useCreateConnection } from "@/react/hooks/connections/useCreateConnection";
import type { CommunityMember } from "@/react/types/CommunityMember";
import { useConnectModal } from "../../../ConnectModal/ConnectModal";
import { Icon } from "../../../Icon";
import { Button } from "../../../uikit/Button";
import { Typography } from "../../../uikit/Typography";

export const ConnectButton = ({
  variant = "secondary",
  recipient,
  recommendationId,
}: {
  variant?: "secondary" | "primary";
  recipient: CommunityMember;
  recommendationId?: number;
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const connectModal = useConnectModal();

  const { mutate: createConnection, isLoading } = useCreateConnection({
    onSuccess: async () => {
      await invalidateAllMemberQueries(queryClient, recipient.public_uid ?? "");
      toast.success(
        t("profile_drawer.connect_banner.messages.connection_created"),
      );
    },
    onError: () => {
      toast.error(
        t("profile_drawer.connect_banner.messages.connection_creation_error"),
      );
    },
  });

  const handleConnect = (note?: string) => {
    createConnection({ memberId: recipient.id, note, recommendationId });
  };

  const openConnectModal = () => {
    void connectModal.show({
      recipient,
      onConnect: handleConnect,
    });
  };

  const handleButtonClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    openConnectModal();
  };

  return (
    <Button
      variant={variant}
      onClick={handleButtonClick}
      aria-label={t("connect")}
      data-testid="connect-button"
      large
      className="!border-primary !h-8 gap-2 !px-4 !font-medium"
      disabled={isLoading}
    >
      {isLoading ? (
        <Icon type="loader" viewBox="0 0 16 16" />
      ) : (
        <>
          <Icon type="16-plus-v2" size={16} useWithFillCurrentColor />
          <Typography.LabelSm color="text-primary" weight="font-medium">
            {t("connect")}
          </Typography.LabelSm>
        </>
      )}
    </Button>
  );
};
